import { t } from '@lingui/macro';
import {
  TDateUnit,
  TDuration,
  TDurationRange,
  TIntroducerApplication,
  TLoanPurpose,
} from 'lendingApi/applications.types';
import { convertCurrencyToNumber } from 'utils/currencyConverter';
import { getDurationInMonths } from 'utils/durationHelper';
import { getLoanDuration } from '../LoanApplication/LoanApplication';
import { TSignupFormData } from '../SignupForm/SignupForm';

export type TApplicationRequestBodyDE = {
  requested_products: {
    amount?: number;
    purpose?: TLoanPurpose;
    detailed_purpose?: string;
    duration?: { amount: number; unit: TDateUnit };
    product_type: 'credit_facility';
  }[];
};

export function normaliseApplication(application?: TIntroducerApplication) {
  const applicationData = application?.requested_products?.[0];

  if (!applicationData) {
    return {};
  }

  return {
    amount: applicationData.amount ? applicationData.amount.toString() : '',
    productType: normaliseProductType(applicationData.product_type),
    loanPurpose: applicationData.purpose || undefined,
    loanPurposeDetails: applicationData.detailed_purpose,
    loanDuration:
      normaliseLoanDuration(applicationData.duration) ||
      getLoanDuration(applicationData.product_type), // Note: Added for old signup issue on not sending a loan duration so it comes in null
  };
}

export function denormaliseApplication(
  formData: Pick<
    TSignupFormData,
    'amount' | 'loanDuration' | 'loanPurpose' | 'loanPurposeDetails'
  >,
) {
  const requestedProducts = [
    {
      amount: convertCurrencyToNumber(formData.amount),
      purpose: formData.loanPurpose || undefined,
      detailed_purpose: formData.loanPurposeDetails,
      duration: denormaliseLoanDuration(formData.loanDuration),
    },
  ];

  return { requested_products: requestedProducts };
}

export function denormaliseApplicationDE(
  formData: Pick<
    TSignupFormData,
    'amount' | 'loanDuration' | 'loanPurpose' | 'loanPurposeDetails'
  >,
): TApplicationRequestBodyDE {
  const requestedProducts: TApplicationRequestBodyDE['requested_products'] = [
    {
      amount: convertCurrencyToNumber(formData.amount, true),
      purpose: formData.loanPurpose || undefined,
      detailed_purpose: formData.loanPurposeDetails,
      duration: denormaliseLoanDuration(formData.loanDuration),
      product_type: 'credit_facility',
    },
  ];

  return { requested_products: requestedProducts };
}

function normaliseProductType(
  type?: TIntroducerApplication['requested_products'][number]['product_type'],
) {
  if (!type) {
    return;
  }

  const allowedProducts = ['credit_facility', 'flexi24'];
  if (allowedProducts.includes(type)) {
    return type;
  }
}

const denormaliseLoanDuration = (
  duration: TDuration | null,
): { amount: number; unit: TDateUnit } | undefined => {
  switch (duration) {
    case '12-months':
      return { amount: 12, unit: 'months' };
    case '24-months':
      return { amount: 24, unit: 'months' };
    case '36-months':
      return { amount: 36, unit: 'months' };
    case '48-months':
      return { amount: 48, unit: 'months' };
    case '60-months':
      return { amount: 60, unit: 'months' };
  }
};

const normaliseLoanDuration = (duration?: {
  amount: number;
  unit: TDateUnit;
}): TDuration | undefined => {
  if (!duration) {
    return;
  }

  const durationInMonths = getDurationInMonths(duration);

  switch (durationInMonths) {
    case 12:
      return '12-months';
    case 24:
      return '24-months';
    case 36:
      return '36-months';
    case 48:
      return '48-months';
    case 60:
      return '60-months';
  }
};

export const normaliseLoanDurationRange = (duration?: {
  amount: number;
  unit: TDateUnit;
}): TDurationRange | undefined => {
  if (!duration) {
    return;
  }

  const durationInMonths = getDurationInMonths(duration);

  switch (durationInMonths) {
    case 12:
      return t`Short term: 1 - 12 months` as TDurationRange;
    case 24:
      return t`Medium term: 13 - 24 months` as TDurationRange;
    case 60:
      return t`Longer term: 36 - 60 months` as TDurationRange;
  }
};
